/**
 * Inline color/size picker for product cards. Cross-filters availability the
 * same way the product page's variant picker does (picking one option can
 * make sibling values in other rows unavailable), then updates a hidden
 * variant-id input inside a real <product-form-component> - the actual
 * add-to-cart submission, cart event dispatch, and error handling are all
 * handled by that existing component, not duplicated here.
 */
class QuickPickerComponent extends HTMLElement {
  connectedCallback() {
    const variantsScript = this.querySelector('[data-quick-picker-variants]');
    if (!variantsScript) return;

    try {
      this.variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      this.variants = [];
    }

    this.rows = Array.from(this.querySelectorAll('.quick-picker__row'));
    this.positions = this.rows.map((row) => row.dataset.optionPosition);
    this.selections = {};

    this.rows.forEach((row) => {
      const selectedButton = row.querySelector('.is-selected');
      if (selectedButton) {
        this.selections[row.dataset.optionPosition] = selectedButton.dataset.quickPickerValue;
      }
    });

    this.addEventListener('click', (event) => this.onClick(event));

    this.updateAvailability();
    this.syncVariant();
    this.previewImage();
  }

  onClick(event) {
    const toggle = event.target.closest('[data-quick-picker-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      const isOpen = this.classList.toggle('is-revealed');
      toggle.setAttribute('aria-expanded', String(isOpen));
      return;
    }

    const button = event.target.closest('[data-quick-picker-value]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const row = button.closest('.quick-picker__row');
    const position = row.dataset.optionPosition;
    this.selections[position] = button.dataset.quickPickerValue;

    row.querySelectorAll('[data-quick-picker-value]').forEach((candidate) => {
      candidate.classList.toggle('is-selected', candidate === button);
    });

    this.updateAvailability();
    this.syncVariant();
    this.previewImage();
  }

  /**
   * Swaps the card's displayed image to match the current (possibly
   * partial) selection, reusing the same <product-card> preview mechanism
   * the theme's own color swatches use - only changes the image when a
   * matching variant actually has a distinct image available.
   *
   * Locks the card's preview to that image (via lockPreviewMedia) rather
   * than just calling previewVariant once: <product-card>'s native
   * "peek second image on hover" behavior runs independently on every
   * pointerenter/pointerleave and knows nothing about this picker's own
   * selection state, so without the lock it would silently overwrite the
   * matched image the next time the card is hovered away from and back
   * onto, leaving the picker showing "Blue, M" selected while the photo
   * quietly reverted to something else.
   */
  previewImage() {
    const productCard = this.closest('product-card');
    if (!productCard) return;

    // Nothing picked yet - leave the card's native hover/peek behavior alone
    // instead of locking to an arbitrary first-matching variant.
    const hasSelection = Object.keys(this.selections).length > 0;
    if (!hasSelection) {
      if (typeof productCard.unlockPreviewMedia === 'function') {
        productCard.unlockPreviewMedia();
      }
      return;
    }

    const candidate =
      this.variants.find((variant) => variant.available && this.variantMatches(variant, this.selections)) ||
      this.variants.find((variant) => this.variantMatches(variant, this.selections));

    if (candidate && candidate.featuredMediaId && typeof productCard.lockPreviewMedia === 'function') {
      productCard.lockPreviewMedia(candidate.featuredMediaId);
    } else if (typeof productCard.unlockPreviewMedia === 'function') {
      productCard.unlockPreviewMedia();
    }
  }

  variantMatches(variant, selections) {
    return this.positions.every((position, index) => {
      const wanted = selections[position];
      return wanted == null || variant.options[index] === wanted;
    });
  }

  updateAvailability() {
    this.rows.forEach((row) => {
      const position = row.dataset.optionPosition;
      row.querySelectorAll('[data-quick-picker-value]').forEach((button) => {
        const candidateSelections = { ...this.selections, [position]: button.dataset.quickPickerValue };
        const hasAvailableVariant = this.variants.some(
          (variant) => variant.available && this.variantMatches(variant, candidateSelections)
        );
        button.classList.toggle('is-unavailable', !hasAvailableVariant);
      });
    });
  }

  syncVariant() {
    const addButton = this.querySelector('[data-quick-picker-add]');
    const variantInput = this.querySelector('[data-quick-picker-variant-id]');
    const label = this.querySelector('[data-quick-picker-add-label]');
    const addContainer = this.querySelector('[data-quick-picker-add-container]');
    if (!addButton || !variantInput || !label) return;

    const allSelected = this.positions.every((position) => this.selections[position] != null);
    if (!allSelected) {
      addButton.disabled = true;
      variantInput.disabled = true;
      label.textContent = 'Select options';
      return;
    }

    const variant = this.variants.find((v) => this.variantMatches(v, this.selections));
    if (!variant) {
      addButton.disabled = true;
      variantInput.disabled = true;
      label.textContent = 'Unavailable';
      return;
    }

    variantInput.value = variant.id;
    variantInput.disabled = false;
    addButton.disabled = !variant.available;
    label.textContent = variant.available ? 'Add to Bag' : 'Sold Out';

    // Keeps the "fly to cart" animation showing the actual selected
    // variant's image rather than whatever the card's default image was.
    if (addContainer && variant.featuredMediaUrl) {
      addContainer.dataset.productVariantMedia = variant.featuredMediaUrl;
    }
  }
}

if (!customElements.get('quick-picker-component')) {
  customElements.define('quick-picker-component', QuickPickerComponent);
}
